import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { changeLanguage } from '../../../application/localization/localization.ts';
import { Language } from '../../../application/localization/locales.ts';
import { logOut, useIsAuthorized } from 'helpers/auth/auth-interceptor.ts';
import styles from './RootPage.module.scss';
import Logger from 'js-logger';
import { errorBoundaryFallbackFunction } from 'components/uikit/suspense/ErrorBoundary.tsx';
import { ErrorBoundary } from '@sentry/react';
import Logo from 'assets/img/logo.svg?react';
import WarningIcon from 'assets/icons/warning-icon.svg?react';
import UserIcon from '../../../assets/icons/user.svg';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { QueryFactory } from '../../../services/api';
import pageStyles from '../warranty-request/WarrantyRequest.module.scss';
import { UserRole } from 'services/api/api-client.ts';
import { Chip } from '../../../components/uikit/pagination/Chip.tsx';
import Footer from '../Footer.tsx';
import LanguageSelector from '../../../components/uikit/LanguageSelector.tsx';
import UserReportModal from './components/UserReportModal.tsx';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { MatomoRouterTracker } from '../../MatomoRouterTracker.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { SearchBar } from './components/SearchBar.tsx';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';
import { Notifications } from '../notifications/Notifications.tsx';
import { Links } from '../../../application/constants/links.ts';
import { queryClient } from '../../../services/api/query-client-helper.ts';
import { tDynamic } from '../../../helpers/tDynamic.ts';

export const UserMenuCategory = 'User menu';
export const AzureAd = 'AzureAd';

const createWarrantyRequestPath = '/create-warranty-request';
const pageDesignerPathRegex = /^\/page-designer\/\S+$/;

export const RootPage = () => {
  const [isLanguageModalShown, setIsLanguageModalShown] = useState(false);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isUserReportModalShown, setIsUserReportModalShown] = useState(false);
  const [shouldPreventLogOut, setShouldPreventLogOut] = useState(false);
  const [isTryingToLogout, setIsTryingToLogout] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();

  const isAuth = useIsAuthorized() === true;
  const userQuery = QueryFactory.UserQuery.useGetCurrentUserInfoQuery({
    enabled: isAuth,
  });

  const firstName = isAuth ? userQuery.data?.firstName : null;
  const email = isAuth ? userQuery.data?.email : null;
  const isAbasUser = isAuth ? !!userQuery.data?.customerNumber : null;
  const userName = isAuth ? userQuery.data?.userName : null;
  const dataRoles = isAuth ? userQuery.data?.claimRoles : null;
  const isAdmin = isAuth
    ? dataRoles?.includes(UserRole.Admin) ||
      !!dataRoles?.includes(UserRole.SemiAdmin)
    : false;
  const roles = dataRoles?.includes(UserRole.Admin)
    ? [UserRole.Admin]
    : dataRoles?.map((role) => role.toString());

  const i18n = useScopedTranslation('Page.RootPage');

  const location = useLocation();
  const isAdvancedSearchPage =
    location.pathname === Links.Authorized.AdvancedSearch.route;

  const IsWelcomePage = location.pathname === '/';
  const IsAdminPage = location.pathname === Links.Authorized.Admin.route;

  useEffect(() => {
    if (
      location.pathname === createWarrantyRequestPath ||
      pageDesignerPathRegex.test(location.pathname)
    ) {
      setShouldPreventLogOut(true);
    } else {
      setShouldPreventLogOut(false);
    }
  }, [location.pathname]);

  const changelanguage = (language: Language) => {
    setIsLanguageModalShown(false);
    closeUserModal();
    changeLanguage(language).catch((e) => Logger.error(e));
  };

  const toggleLanguageModal = () => {
    setIsLanguageModalShown((prevState) => !prevState);
  };

  const toggleUserReportModal = () => {
    setIsUserReportModalShown((prevState) => !prevState);
    closeUserModal();
  };

  const logout = () => {
    setIsLanguageModalShown(false);
    setIsModalShown(false);
    logOut()
      .then(() => queryClient.clear())
      .catch((e) => Logger.error(e));
  };

  const handleLogout = () => {
    if (!shouldPreventLogOut) {
      logout();
    } else {
      setIsTryingToLogout(true);
    }
  };

  const closeUserModal = () => {
    setIsModalShown(false);
  };

  const isWarningMessageEnabled = FeatureFlags.isWarningMessageEnabled();
  const warningMessageTranslationKey =
    FeatureFlags.warningMessageTranslationKey();

  return (
    <div
      onClick={() => {
        setIsLanguageModalShown(false);
        setIsModalShown(false);
      }}
      className={styles.wrapper}
      data-test-id="main-page-container"
    >
      <MatomoRouterTracker />
      <div className={clsx(styles.rowContainer4, styles.relative)}>
        <div>
          <Link to={'/'}>
            <Logo />
          </Link>
        </div>
        {(IsWelcomePage || IsAdminPage) && isAdmin && (
          <div className={clsx(styles.rowContainer4, styles.chipsContainer)}>
            <Chip
              value={i18n.t('chips_admin')}
              isActive={IsAdminPage}
              onClickHandler={() => {
                void navigate(Links.Authorized.Admin.route);
              }}
            />
            <Chip
              value={i18n.t('chips_customer')}
              isActive={IsWelcomePage}
              onClickHandler={() => {
                void navigate(Links.Authorized.Welcome.route);
              }}
            />
          </div>
        )}
        <div className={styles.rowContainer4}>
          {FeatureFlags.isGlobalSearchEnabled() && !isAdvancedSearchPage && (
            <div className={styles.marginRight16}>
              <SearchBar />
            </div>
          )}
          {FeatureFlags.isNotificationsEnabled() && <Notifications />}
          <div>
            {!isAuth && (
              <LanguageSelector
                changeLanguage={changelanguage}
                isInMenu={false}
                isLanguageModalShown={isLanguageModalShown}
                toggleLanguageModal={toggleLanguageModal}
              />
            )}
            {isAuth && (
              <IconButton
                className={clsx(
                  styles.borderRadius,
                  isModalShown && styles.selected,
                )}
                testId={'user-icon'}
                icon={UserIcon}
                iconClassName={styles.icon}
                onClick={(e) => {
                  trackEvent({
                    category: UserMenuCategory,
                    action: 'Open user menu',
                    name: 'Open user menu',
                    documentTitle: document.title,
                    href: window.location.href,
                  });
                  setIsModalShown((prevState) => !prevState);
                  setIsLanguageModalShown(false);
                  e.stopPropagation();
                }}
              ></IconButton>
            )}
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={styles.relative}
          >
            {isModalShown && (
              <div className={styles.userModalBody}>
                <div className={clsx(styles.divider, styles.userModalItem)}>
                  <div className={styles.p1SemiB}>{firstName}</div>
                  <div className={styles.greyText}>
                    {isAbasUser ? userName : email}
                  </div>
                  <div className={styles.greyText}>{roles?.join(', ')}</div>
                </div>
                <div
                  className={clsx(
                    styles.userModalItem,
                    styles.cursor,
                    styles.divider,
                  )}
                  data-test-id="account-settings-link"
                  onClick={() => {
                    void navigate(Links.Authorized.AccountSettings.route);
                    closeUserModal();
                  }}
                >
                  {i18n.t('account_settings')}
                </div>
                <div className={styles.divider}>
                  <LanguageSelector
                    changeLanguage={changelanguage}
                    isInMenu={true}
                    isLanguageModalShown={isLanguageModalShown}
                    toggleLanguageModal={toggleLanguageModal}
                  />
                  <div
                    onClick={() => {
                      toggleUserReportModal();
                      trackEvent({
                        category: UserMenuCategory,
                        action: 'Open report issue modal',
                        name: 'Open report issue modal',
                      });
                    }}
                    className={clsx(styles.userModalItem, styles.cursor)}
                  >
                    {i18n.t('report_issue')}
                  </div>
                </div>
                <div
                  onClick={() => {
                    handleLogout();
                    trackEvent({
                      category: UserMenuCategory,
                      action: 'Click logout',
                      name: 'Click logout',
                    });
                  }}
                  className={clsx(styles.userModalItem, styles.cursor)}
                  data-test-id={'logout'}
                >
                  {i18n.t('logout')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isUserReportModalShown && (
        <UserReportModal
          userQuery={userQuery}
          onClose={() => {
            setIsUserReportModalShown(false);
          }}
          onSubmit={() => {
            setIsUserReportModalShown(false);
            setIsTooltipVisible(true);
            setTimeout(() => {
              setIsTooltipVisible(false);
            }, 2500);
          }}
        />
      )}
      {isWarningMessageEnabled && (
        <div className={styles.warningContainer}>
          <WarningIcon className={styles.warningIcon} />
          <div className={styles.warningText}>
            {tDynamic(i18n, warningMessageTranslationKey, {
              defaultValue: warningMessageTranslationKey,
            })}
          </div>
        </div>
      )}
      <div className={styles.content}>
        <ErrorBoundary fallback={errorBoundaryFallbackFunction}>
          <Outlet
            context={{
              logout,
              isTryingToLogout,
              setIsTryingToLogout,
              setShouldPreventLogOut,
            }}
          />
        </ErrorBoundary>
      </div>
      {isTooltipVisible && (
        <Tooltip
          closeTooltip={() => setIsTooltipVisible(false)}
          bodyContent={<div>{i18n.t('tooltip_message')}</div>}
          className={pageStyles.tooltip}
          showCancelButton={true}
        />
      )}
      <Footer />
    </div>
  );
};
